import { PageHeader, SaveCancelFooter } from '@/components/molecules'
import { TargetSetUp } from '@/containers'
import { useAppDispatch, useAppSelector } from '@/store'
import { useState, useEffect } from 'react'
import { resetSalesTarget, setSalesTargetFormData } from '@/store/slices/salesTargetSlice'
import { ProgressIndicator, Breadcrumb, Modal } from '@/components/atoms'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/utils/constants/routesConstants'
import { setToastNotifications } from '@/store/slices/commonSlice'
import { MODAL_TYPE } from '@/utils/constants/globalStyle'
import { getISOTimeString, getUUID } from '@/utils/commons'
import { createSalesTargetRequestObject } from './SalesTargetUtils'
import { TARGET_TYPES } from '@/utils/constants/saleTargetConstants'
import { addDraftSalesTargetList, updateDraftSalesTargetList } from '@/services/salesTargets'
import { useLocation } from 'react-router-dom';


const AddSalesTarget = () => {
  const location = useLocation();
  const isEdit = location?.state?.isEdit ?? false;
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const salesTarget = useAppSelector(state => state?.salesTarget)
  const [isCancel, setIsCancel] = useState(false)
  const [formData, setFormData] = useState(salesTarget?.formData)
  const [isPrerequisiteTarget, setIsPrerequisiteTarget] = useState(false)

  useEffect(() => {
    setIsPrerequisiteTarget(salesTarget.targetType === TARGET_TYPES.MonthlyTargetPrerequistite || salesTarget.targetType === TARGET_TYPES.ProductTargetPrerequisite)
  }, [salesTarget.targetType])

  const handleNextAction = () => {
    const data = { ...formData }
    data.displayStartDate = data?.activeDisplay ? data?.startDate : data?.displayStartDate
    data.displayEndDate = data?.activeDisplay ? data?.endDate : data?.displayEndDate
    dispatch(setSalesTargetFormData(data))
    navigate(ROUTES.SALES_TARGET_DISPLAY_CONTENT, { state: { isEdit: isEdit } })
  }

  const {
    targetType,
    targetNameValid,
    startDateValid,
    endDateValid,
    activeDisplay,
    displayStartDateValid,
    displayEndDateValid,
    targetEndDateValid,
    awardPoint,
    pointsDeliveryDateValid,
    roundUpPointsFlag,
    roundOffValueValid,
    pointsDeliveryDate,
    isNumberOfLevelsSelected,
    isNumberOfMilestonesSelected
  } = formData

  const handleDraft = async () => {
    const isTargetNameChanged = salesTarget.initialTargetName === null || salesTarget.initialTargetName !== formData?.targetName;
    const data = { ...formData }
    data.displayStartDate = getISOTimeString(data?.activeDisplay ? data?.startDate : data?.displayStartDate, false, true)
    data.displayEndDate = getISOTimeString(data?.activeDisplay ? data?.endDate : data?.displayEndDate, false, true)
    data.pointsDeliveryDate = getISOTimeString(data?.pointsDeliveryDate, false, true);
    data.startDate = getISOTimeString(data?.startDate, false, true);
    data.endDate = getISOTimeString(data?.endDate, false, true);
    data.targetEndDate = getISOTimeString(data?.targetEndDate, false, true);
    data.activeStartDate = getISOTimeString(data?.activeStartDate, false, true);
    data.activeEndDate = getISOTimeString(data?.activeEndDate, false, true);
    data.targetDeliveryDate = getISOTimeString(data?.targetDeliveryDate, false, true);
    data.targetType = data?.targetTypeValue || "";
    const requestData = createSalesTargetRequestObject(data, isPrerequisiteTarget)
    requestData.uuid = isTargetNameChanged ? getUUID(data?.targetName) : getUUID(salesTarget.initialTargetName);
    requestData.status = 'Draft';

    let response;
    if (isTargetNameChanged) {
      response = await addDraftSalesTargetList({ data: requestData })
    } else {
      response = await updateDraftSalesTargetList({ data: requestData })
    }
    if (response.success) {
      dispatch(resetSalesTarget())
      dispatch(setToastNotifications({ show: true, message: 'New Sales Target Drafted', type: 'info' }))
      navigate(ROUTES.SALES_TARGETS)
    } else dispatch(setToastNotifications({ show: true, message: 'New Sales Target not Drafted', type: 'error' }))
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }
  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }
  const onClickPrimaryButton = () => {
    dispatch(resetSalesTarget())
    navigate(ROUTES.SALES_TARGETS)
  }

  const validateFormData = isValid => {
    return isValid === undefined || isValid === true
  }

  const commonFieldsValidation = () => {

    return !targetType ||
      validateFormData(targetNameValid) ||
      validateFormData(startDateValid) ||
      validateFormData(endDateValid) ||
      validateFormData(targetEndDateValid) ||
      validateFormData(isNumberOfLevelsSelected) ||
      validateFormData(isNumberOfMilestonesSelected)
  }

  const getFormError = () => {

    const hasCommonError = commonFieldsValidation();

    if (
      !activeDisplay &&
      (hasCommonError || validateFormData(displayStartDateValid) || validateFormData(displayEndDateValid))
    ) {
      return true;
    }

    if (!awardPoint && (hasCommonError || !pointsDeliveryDate || validateFormData(pointsDeliveryDateValid))) {
      return true;
    }

    if (roundUpPointsFlag) {
      const roundOffError = hasCommonError || validateFormData(roundOffValueValid);
      return roundOffError;
    }

    if (salesTarget.storeList.length === 0) {
      return true;
    }
    return hasCommonError;
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },
            {
              href: '',
              label: 'Loyalty - Earn Points',
            },
            {
              href: '',
              label: 'Add Sales Target Mexico',
            },
          ],
        }}
        id={'ast_bradcrumb'}
      />
      <PageHeader title={'Add Sales Target'} />

      <ProgressIndicator
        currentStep={1}
        completed={false}
        orientation="horizontal"
        steps={4}
        titles={[{ title: 'Setup' }, { title: 'Display Content' }, { title: 'Products' }, { title: 'Review' }]}
        className="mb-6"
      />
      <br />
      <TargetSetUp setFormData={setFormData} formData={formData} />
      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title="Are you sure you want to cancel?"
          text={'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'}
          showPrimaryButton={true}
          primaryButtonText="Yes"
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText="No"
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary',
          }}
          isDark={true}
        />
      ) : null}

      <SaveCancelFooter
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraft}
        nextAction={handleNextAction}
      />
    </>
  )
}

export default AddSalesTarget
